import { Component } from '@theme/component';
import { getIntersectionRoot, getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';

/**
 * Scroll-scrubbed store entrance.
 *
 * Drives an <video> element's currentTime from the scroll position of a tall
 * track, then cross-fades the final frames into a matching interior still so the
 * clip never visibly "ends". The still image underneath the video means the very
 * first paint is already the finished storefront frame — no black video box.
 *
 * Horizon uses two scroll models: below 990px the window scrolls, at and above
 * 990px `html`/`body` are `overflow: hidden` and `.page-wrapper` is the scroller
 * (see base.css). Progress is *measured* with `getBoundingClientRect()`, which is
 * correct under both, but the update has to be *driven* by whichever element
 * actually scrolls, so the scroll listener and the IntersectionObserver root both
 * come from the theme's own scroll-container helpers. Listening on `window` or
 * leaving the observer root implicit leaves the entrance inert on desktop, where
 * neither one ever sees the scroll.
 *
 * Scroll events update synchronously. The rAF loop only carries the eased playhead
 * between them, so the scrub never depends on frame delivery for correctness — an
 * entrance that moves only while frames happen to be served is an entrance that
 * silently does nothing whenever they are not.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} track - The tall scroll container.
 * @property {HTMLElement} viewport - The sticky viewport pinned inside the track.
 * @property {HTMLVideoElement} [video] - The entrance clip.
 * @property {HTMLElement} [interior] - The interior still layered over the video.
 * @property {HTMLElement} [indicator] - The "scroll to enter" affordance.
 * @property {HTMLElement} [introLogo] - The optional logo overlay.
 *
 * @extends Component<Refs>
 */
export class FbStoreEntranceComponent extends Component {
  requiredRefs = ['track', 'viewport'];

  /** Half a frame at 24fps. Below this, a seek is not worth issuing. */
  static SEEK_EPSILON = 0.02;

  /** Progress (0-1) over which the scroll affordance fades away. */
  static INDICATOR_FADE = 0.05;

  #rafId = 0;
  /** Shared clock for both update paths, so easing advances once per elapsed ms. */
  #lastAdvanceTs = 0;
  #inView = false;
  #duration = 0;
  #targetTime = 0;
  #easedTime = 0;
  #smoothing = 0.15;
  #crossfadeStart = 0.86;
  #hold = 0.12;
  #videoReady = false;
  #priming = false;
  #seekabilityChecked = false;
  /** Blob URL standing in for a clip the server will not serve seekably. */
  #objectUrl = '';
  /** The time most recently handed to the element, so an in-flight seek can be superseded. */
  #pendingSeek = -1;
  #arrived = false;
  #past = false;
  #trackHeight = 0;
  #viewportHeight = 0;
  #lastInteriorOpacity = -1;
  #lastFadeOpacity = -1;
  /** @type {IntersectionObserver | undefined} */
  #intersectionObserver;
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;
  /** @type {EventTarget | undefined} */
  #scrollTarget;
  /** @type {AbortController | undefined} */
  #controller;

  connectedCallback() {
    super.connectedCallback();

    try {
      this.#init();
    } catch (error) {
      // A broken entrance must never trap the customer above the fold.
      console.error('[fb-store-entrance] initialisation failed, releasing the page', error);
      this.#settle();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stopLoop();
    this.#intersectionObserver?.disconnect();
    this.#resizeObserver?.disconnect();
    this.#controller?.abort();
    if (this.#objectUrl) URL.revokeObjectURL(this.#objectUrl);
  }

  #init() {
    const { track, viewport, video } = this.refs;

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#smoothing = readSmoothing(this.dataset.smoothness);
    this.#crossfadeStart = clamp(Number(this.dataset.crossfadeStart ?? 86) / 100, 0, 0.98);
    this.#hold = clamp(Number(this.dataset.hold ?? 12) / 100, 0, 0.6);

    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    const isStatic = this.dataset.fbMode === 'static' || !(video instanceof HTMLVideoElement);

    // Re-run in both directions, so toggling the OS setting mid-session doesn't
    // strand the customer on whichever version happened to load first.
    reducedMotion.addEventListener('change', () => this.#reinitialise(), { once: true, signal });

    // Reduced motion and "no video configured" both resolve to the same place:
    // the finished interior, with the page immediately scrollable.
    if (reducedMotion.matches || isStatic) {
      this.dataset.fbMode = reducedMotion.matches ? 'reduced' : 'static';
      this.#settle();
      return;
    }

    this.dataset.fbMode = 'scrub';
    this.#setupVideo(video, signal);

    this.#alignToViewportTop();
    this.#viewportHeight = viewport.offsetHeight;
    this.#trackHeight = track.offsetHeight;

    this.#resizeObserver = new ResizeObserver(() => {
      this.#alignToViewportTop();
      this.#viewportHeight = viewport.offsetHeight;
      this.#trackHeight = track.offsetHeight;
      this.#advance();
    });
    this.#resizeObserver.observe(viewport);
    this.#resizeObserver.observe(track);

    this.#intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        this.#inView = entry.isIntersecting;
        if (this.#inView) {
          this.#startLoop();
        } else {
          // Sync once more before going idle: a fast flick can otherwise leave
          // the header frosted because the final frame never ran.
          this.#advance();
          this.#stopLoop();
        }
      },
      // Without an explicit root this observes the document viewport, which at
      // >=990px is not the scrollport — `.page-wrapper` scrolls inside it.
      { root: getIntersectionRoot(), rootMargin: '150px 0px' }
    );
    this.#intersectionObserver.observe(track);

    // What actually drives the entrance. It must be bound to the element that
    // scrolls: `window` never fires a scroll event in the squeeze layout, so
    // binding there is indistinguishable from not scrubbing at all.
    this.#scrollTarget = getScrollEventTarget();
    this.#scrollTarget.addEventListener('scroll', this.#onScroll, { passive: true, signal });

    // Crossing 990px swaps the scroller out from under both bindings above.
    scrollContainerMediaQuery.addEventListener('change', () => this.#reinitialise(), { once: true, signal });

    // Cover the restored-scroll case: the customer may land already past the
    // entrance, in which case the observer never reports an intersection.
    this.#advance();

    // Optional chaining does not guard an undeclared identifier, so go via window.
    if (window.Shopify?.designMode) {
      for (const event of ['shopify:block:select', 'shopify:section:select']) {
        this.addEventListener(event, () => this.#settle(), { signal });
      }
    }
  }

  /**
   * Cancel the vertical space the header group reserves above the entrance.
   *
   * The CSS fallback uses `--header-group-height`, which is good enough to stop
   * a white bar on the first paint but is not exact — Horizon recomputes it with
   * different semantics after load (observed: 125px against 109.375px of real
   * reserved space). Summing the header group's children is the measurement that
   * actually means "space above the entrance", and it stays correct whether the
   * header is sticky, static, or absolutely positioned by transparent mode.
   */
  #alignToViewportTop() {
    if (this.dataset.fbMode !== 'scrub') return;

    const headerGroup = document.getElementById('header-group');
    if (!headerGroup) return;

    // getBoundingClientRect, not offsetHeight: the latter rounds to whole pixels
    // (43 + 66 against a true 43 + 66.375) and the lost fraction shows up as a
    // hairline strip of page background above the storefront.
    let reserved = 0;
    for (const child of headerGroup.children) {
      if (child instanceof HTMLElement) reserved += child.getBoundingClientRect().height;
    }

    this.style.marginBlockStart = `${-reserved}px`;
  }

  #reinitialise() {
    this.#controller?.abort();
    this.#intersectionObserver?.disconnect();
    this.#resizeObserver?.disconnect();
    this.#stopLoop();
    this.#arrived = false;
    this.#past = false;
    this.#videoReady = false;
    this.#priming = false;
    this.#pendingSeek = -1;
    this.#lastAdvanceTs = 0;
    this.#lastInteriorOpacity = -1;
    this.#lastFadeOpacity = -1;
    delete this.dataset.fbArrived;
    delete this.dataset.fbPast;
    delete this.dataset.fbContent;
    this.#init();
  }

  /**
   * @param {HTMLVideoElement} video
   * @param {AbortSignal} signal
   */
  #setupVideo(video, signal) {
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.loop = false;
    video.controls = false;
    video.pause();

    // Scroll owns the playhead; nothing else is allowed to advance it. The
    // priming pass is the one exception — pausing it from here would reject its
    // own play() promise and abort the decoder warm-up it exists to perform.
    video.addEventListener(
      'play',
      () => {
        if (!this.#priming) video.pause();
      },
      { signal }
    );

    video.addEventListener(
      'loadedmetadata',
      () => {
        const duration = video.duration;
        if (!Number.isFinite(duration) || duration <= 0) return;
        this.#duration = duration;
        this.#prime(video);
        this.#advance();
      },
      { signal }
    );

    // `readyState >= 2` is the first moment a decoded frame actually exists, so
    // it is the only safe moment to fade the video up over the poster still.
    for (const event of ['loadeddata', 'seeked', 'canplay']) {
      video.addEventListener(event, () => {
        this.#markVideoReady(video);
        this.#ensureSeekable(video);
      }, { signal });
    }

    video.addEventListener('error', () => this.#settle(), { signal });

    if (video.readyState >= 1 && Number.isFinite(video.duration) && video.duration > 0) {
      this.#duration = video.duration;
      this.#prime(video);
    }

    // Safari (iOS especially) will not decode a frame for a paused, never-played
    // element, so a scrub-only video can stay blank. A muted play/pause pair
    // primes the decoder; a first touch is the fallback if autoplay is refused.
    // Not `once`: if autoplay is refused the first time, the next gesture should
    // get another go. #prime itself is the guard against repeat work.
    const primeOnIntent = () => this.#prime(video);
    for (const event of ['pointerdown', 'touchstart', 'wheel', 'keydown']) {
      window.addEventListener(event, primeOnIntent, { passive: true, signal });
    }
  }

  /** @param {HTMLVideoElement} video */
  #markVideoReady(video) {
    if (this.#videoReady || video.readyState < 2) return;
    this.#videoReady = true;
    this.dataset.fbVideo = 'ready';
  }

  /**
   * Guarantee the clip can actually be scrubbed.
   *
   * Seeking a progressive mp4 requires the server to answer range requests. When
   * it does not, Chrome reports an empty `seekable` range and then *silently
   * ignores* every `currentTime` assignment — no error, no event, the playhead
   * simply never moves. The scrub maths still runs perfectly and the storefront
   * sits frozen on frame one, which is a genuinely hard failure to read from the
   * outside. `shopify theme dev` serves assets with plain 200s, so this is the
   * normal local condition rather than an edge case.
   *
   * Re-serving the clip from a blob URL sidesteps the server entirely: an
   * in-memory resource is always fully seekable. The bytes are already in the
   * HTTP cache from `preload="auto"`, so this is usually not a second download.
   * Where ranges *are* supported (the Shopify CDN in production) the first check
   * passes and none of this runs.
   *
   * @param {HTMLVideoElement} video
   */
  async #ensureSeekable(video) {
    if (this.#seekabilityChecked || video.readyState < 2) return;
    this.#seekabilityChecked = true;

    const { seekable } = video;
    if (seekable.length > 0 && seekable.end(seekable.length - 1) > 0) return;

    const source = video.currentSrc || video.src;
    if (!source || source.startsWith('blob:')) return;

    try {
      const response = await fetch(source);
      if (!response.ok) return;

      this.#objectUrl = URL.createObjectURL(await response.blob());

      // Drop back to the poster while the element re-reads the clip, so the
      // stage never shows a half-loaded video.
      this.#videoReady = false;
      this.#priming = false;
      this.#pendingSeek = -1;
      delete this.dataset.fbVideo;

      video.src = this.#objectUrl;
      video.load();
    } catch (error) {
      // Leave the direct source in place; the entrance still shows the stills.
      console.warn('[fb-store-entrance] could not re-serve the clip for scrubbing', error);
    }
  }

  /**
   * Warm the decoder so a frame exists to scrub to. Once a frame is decoded
   * (`#videoReady`) there is nothing left to prime.
   *
   * @param {HTMLVideoElement} video
   */
  #prime(video) {
    if (this.#videoReady || this.#priming) return;
    this.#priming = true;

    const settle = () => {
      this.#priming = false;
      video.pause();
      video.currentTime = this.#easedTime;
    };

    const played = video.play();
    if (played && typeof played.then === 'function') played.then(settle, settle);
    else settle();
  }

  #startLoop() {
    if (this.#rafId) return;
    this.#rafId = requestAnimationFrame(this.#tick);
  }

  #stopLoop() {
    if (!this.#rafId) return;
    cancelAnimationFrame(this.#rafId);
    this.#rafId = 0;
  }

  /**
   * Scroll is the source of truth. Updating synchronously here means the playhead
   * tracks the scroll even when frames are not being served (a background tab, a
   * throttled renderer), rather than waiting for a frame that may never arrive.
   */
  #onScroll = () => {
    this.#advance();
    // Carry the remaining ease once the scroll itself has stopped.
    this.#startLoop();
  };

  /** Advance the animation by however long has actually elapsed. */
  #advance() {
    const now = performance.now();
    const delta = this.#lastAdvanceTs ? Math.min(now - this.#lastAdvanceTs, 100) : 16.67;
    this.#lastAdvanceTs = now;
    this.#update(delta);
  }

  #tick = () => {
    this.#rafId = 0;
    this.#advance();

    // Keep going while the entrance is on screen, and otherwise for just long
    // enough to let the eased playhead land on its target.
    const easing = Math.abs(this.#targetTime - this.#easedTime) > FbStoreEntranceComponent.SEEK_EPSILON;
    if (this.#inView || easing) this.#rafId = requestAnimationFrame(this.#tick);
  };

  /**
   * One read pass, then one write pass. Geometry that only changes on resize is
   * cached by the ResizeObserver so the per-frame cost is a single rect read.
   *
   * @param {number} delta - Milliseconds since the previous frame.
   */
  #update(delta) {
    const { track, video, interior, indicator, introLogo } = this.refs;

    // --- read ---
    const top = track.getBoundingClientRect().top;
    const span = this.#trackHeight - this.#viewportHeight;
    const progress = span > 0 ? clamp(-top / span, 0, 1) : top <= 0 ? 1 : 0;

    // The tail of the track is a deliberate pause on the finished interior, so
    // the clip has to be fully played by the time the hold begins.
    const playable = 1 - this.#hold;
    const videoProgress = playable > 0 ? clamp(progress / playable, 0, 1) : 1;

    this.#targetTime = videoProgress * this.#duration;

    // Frame-rate independent exponential approach, so the weight of the scrub
    // feels the same at 60Hz and 120Hz.
    const factor = 1 - Math.pow(1 - this.#smoothing, delta / (1000 / 60));
    this.#easedTime += (this.#targetTime - this.#easedTime) * factor;
    if (Math.abs(this.#targetTime - this.#easedTime) < FbStoreEntranceComponent.SEEK_EPSILON / 2) {
      this.#easedTime = this.#targetTime;
    }

    const crossfadeSpan = 1 - this.#crossfadeStart;
    const rawFade = crossfadeSpan > 0 ? clamp((videoProgress - this.#crossfadeStart) / crossfadeSpan, 0, 1) : 1;
    const fade = smoothstep(rawFade);

    // --- write ---
    // Seeking is gated on metadata, not on the ready flag: the flag is itself
    // set by the events a seek produces, so waiting for it would deadlock.
    if (video instanceof HTMLVideoElement && this.#duration > 0 && video.readyState >= 1) {
      // Compare against whatever is already in flight rather than against the
      // element's current position. Waiting for `seeked` before accepting a
      // newer target is what produces the staircase: the playhead chases a
      // position the scroll left behind several frames ago. Assigning over an
      // in-flight seek supersedes it, so the pending target is always the
      // freshest one and at most one seek is ever outstanding.
      const reference = video.seeking ? this.#pendingSeek : video.currentTime;

      if (Math.abs(this.#easedTime - reference) > FbStoreEntranceComponent.SEEK_EPSILON) {
        this.#pendingSeek = this.#easedTime;
        video.currentTime = this.#easedTime;
      }
    }

    if (interior && Math.abs(fade - this.#lastInteriorOpacity) > 0.003) {
      this.#lastInteriorOpacity = fade;
      interior.style.opacity = String(fade);
    }

    const affordance = clamp(1 - progress / FbStoreEntranceComponent.INDICATOR_FADE, 0, 1);
    if (Math.abs(affordance - this.#lastFadeOpacity) > 0.01) {
      this.#lastFadeOpacity = affordance;
      if (indicator) indicator.style.opacity = String(affordance);
      if (introLogo) introLogo.style.setProperty('--fb-intro-logo-scroll', String(affordance));
    }

    this.#setArrived(fade >= 0.9);
    this.#setPast(progress >= 0.999);
  }

  /** @param {boolean} value */
  #setArrived(value) {
    if (value === this.#arrived) return;
    this.#arrived = value;

    if (value) {
      this.dataset.fbArrived = '';
      this.dataset.fbContent = 'in';
    } else {
      // Walking back out has to take the showroom copy with it, otherwise the
      // hero text is left floating over the closed storefront.
      delete this.dataset.fbArrived;
      delete this.dataset.fbContent;
    }
  }

  /** @param {boolean} value */
  #setPast(value) {
    if (value === this.#past) return;
    this.#past = value;
    if (value) this.dataset.fbPast = '';
    else delete this.dataset.fbPast;
  }

  /**
   * Jump to the finished state: interior visible, content revealed, header
   * released. Used for reduced motion, missing/broken video, and the theme
   * editor's block selection.
   */
  #settle() {
    const { interior } = this.refs;
    if (interior) interior.style.opacity = '1';
    this.#lastInteriorOpacity = 1;
    this.#setArrived(true);
    this.dataset.fbContent = 'in';
    this.dataset.fbSettled = '';
  }
}

/**
 * Maps the merchant-facing 0-100 "smoothness" dial onto a per-frame lerp factor.
 * Higher settings mean more weight, so the factor falls as the setting rises.
 *
 * @param {string | undefined} value
 * @returns {number}
 */
function readSmoothing(value) {
  const setting = clamp(Number(value ?? 65), 0, 100);
  return 0.02 + (1 - setting / 100) * 0.38;
}

/**
 * @param {number} value
 * @param {number} min
 * @param {number} max
 * @returns {number}
 */
function clamp(value, min, max) {
  if (!Number.isFinite(value)) return min;
  return Math.min(Math.max(value, min), max);
}

/**
 * @param {number} t
 * @returns {number}
 */
function smoothstep(t) {
  return t * t * (3 - 2 * t);
}

if (!customElements.get('fb-store-entrance')) {
  customElements.define('fb-store-entrance', FbStoreEntranceComponent);
}
